/**
 * LandingPage — public marketing surface at /.
 *
 * Blueprint: /app/design_guidelines.json (Swiss-Brutalist, terminal edge,
 * left-aligned typography, emerald/blue accents, no AI-slop gradients).
 *
 * All animations are pure CSS keyframes (no framer-motion dep). Every
 * interactive surface carries a data-testid for regression automation.
 */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ShieldCheck,
  ArrowRight,
  KeyRound,
  Fingerprint,
  Timer,
  AlertTriangle,
  Eye,
  EyeOff,
  Monitor,
  VenetianMask,
  Check,
  X as XIcon,
  ChevronDown,
  Download,
  Apple,
  Terminal,
  Github,
  Scale,
  FileText,
  Sparkles,
  Mic,
  FileSearch,
  Atom,
  Radio,
  Activity,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import { GovernmentDisclaimer } from '../components/GovernmentDisclaimer';
import { WarrantCanaryChip } from '../components/WarrantCanary';
import './landing.css';

// ─── Section: NAV ─────────────────────────────────────────────────

const Nav = () => (
  <header className="sticky top-0 z-40 bg-black/60 backdrop-blur-xl border-b border-white/10">
    <div className="max-w-7xl mx-auto px-6 lg:px-12 py-4 flex items-center justify-between">
      <Link
        to="/"
        className="flex items-center gap-2 group"
        data-testid="nav-logo"
      >
        <ShieldCheck className="w-5 h-5 text-primary" strokeWidth={1.75} />
        <span className="text-sm font-semibold tracking-tight">
          Government Shield
        </span>
      </Link>

      <nav className="hidden md:flex items-center gap-8 text-sm text-zinc-400">
        <a href="#how" className="hover:text-white transition-colors" data-testid="nav-how-link">
          How it works
        </a>
        <a href="#features" className="hover:text-white transition-colors" data-testid="nav-features-link">
          Features
        </a>
        <Link to="/manifesto" className="hover:text-white transition-colors" data-testid="nav-manifesto-link">
          Manifesto
        </Link>
        <Link to="/halo" className="hover:text-white transition-colors" data-testid="nav-halo-link">
          Halo
        </Link>
        <a href="#faq" className="hover:text-white transition-colors" data-testid="nav-faq-link">
          FAQ
        </a>
      </nav>

      <div className="flex items-center gap-2">
        <Button
          asChild
          variant="ghost"
          size="sm"
          className="text-zinc-400 hover:text-white"
          data-testid="nav-login-link"
        >
          <Link to="/login">Sign in</Link>
        </Button>
        <Button
          asChild
          size="sm"
          className="bg-primary hover:bg-primary/90"
          data-testid="nav-waitlist-btn"
        >
          <Link to="/waitlist">Request access</Link>
        </Button>
      </div>
    </div>
  </header>
);

// ─── Section: HERO ─────────────────────────────────────────────────

const Hero = () => (
  <section className="relative overflow-hidden" data-testid="landing-hero">
    {/* Background: radial gradient + grain */}
    <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_left,rgba(59,130,246,0.09),transparent_60%)] pointer-events-none" />
    <div className="absolute inset-0 gs-grain pointer-events-none opacity-[0.04]" />

    <div className="relative max-w-7xl mx-auto px-6 lg:px-12 pt-24 pb-32 sm:pt-32 sm:pb-40">
      <div className="grid lg:grid-cols-12 gap-8 items-start">
        <div className="lg:col-span-8">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-white/10 bg-white/[0.02] text-[11px] uppercase tracking-[2px] text-zinc-400 mb-8">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
            Invite-only · closed beta
          </div>

          <h1 className="text-5xl sm:text-6xl lg:text-7xl font-black tracking-tighter leading-[0.95] text-white">
            Private by design.
            <br />
            <span className="text-primary">Sovereign</span> by default.
          </h1>

          <p className="mt-8 text-lg text-zinc-400 max-w-xl leading-relaxed">
            An encrypted, invite-only messenger. Keys never leave your device.
            Messages auto-destruct after 24 hours. No biometrics. No analytics.
            No archive.
          </p>

          <div className="mt-10 flex flex-wrap gap-3">
            <Button
              asChild
              size="lg"
              className="bg-primary hover:bg-primary/90 text-white font-semibold gap-2 rounded-md"
              data-testid="hero-waitlist-btn"
            >
              <Link to="/waitlist">
                Request access
                <ArrowRight className="w-4 h-4" />
              </Link>
            </Button>
            <Button
              asChild
              size="lg"
              variant="outline"
              className="border-white/15 bg-transparent hover:bg-white/5 text-white rounded-md"
              data-testid="hero-how-it-works-btn"
            >
              <a href="#how">See how it works</a>
            </Button>
          </div>

          {/* Key facts band */}
          <div className="mt-16 grid grid-cols-3 gap-6 max-w-xl">
            {[
              { label: '256-bit', sub: 'AES-GCM' },
              { label: '24h', sub: 'auto-destruct' },
              { label: 'E2E', sub: 'always-on' },
            ].map((f) => (
              <div key={f.label} className="border-l border-white/10 pl-4">
                <div className="text-2xl font-bold tracking-tight text-white">
                  {f.label}
                </div>
                <div className="text-[11px] uppercase tracking-[1.5px] text-zinc-500 mt-1">
                  {f.sub}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Terminal card — right column on desktop */}
        <div className="lg:col-span-4 hidden lg:block">
          <div className="rounded-md border border-white/10 bg-[#0A0A0F] overflow-hidden shadow-[inset_0_1px_0_rgba(255,255,255,0.05)]">
            <div className="flex items-center gap-1.5 px-3 py-2 border-b border-white/5 bg-white/[0.02]">
              <span className="w-2.5 h-2.5 rounded-full bg-red-500/60" />
              <span className="w-2.5 h-2.5 rounded-full bg-yellow-500/60" />
              <span className="w-2.5 h-2.5 rounded-full bg-emerald-500/60" />
              <span className="ml-2 text-[10px] font-mono text-zinc-600">
                handshake.log
              </span>
            </div>
            <div className="p-4 font-mono text-[11px] leading-relaxed">
              <div className="text-zinc-500">$ connect rtest2@shield</div>
              <div className="text-zinc-400 mt-1">resolving public key…</div>
              <div className="text-emerald-400 mt-1">
                ✓ fingerprint{' '}
                <span className="text-emerald-300">6f5b0cb594d3c80e</span>
              </div>
              <div className="text-zinc-400 mt-1">
                generating session key AES-256…
              </div>
              <div className="text-emerald-400 mt-1">✓ sealed</div>
              <div className="text-zinc-400 mt-1">
                ttl: <span className="text-zinc-200">23h 59m 58s</span>
              </div>
              <div className="text-zinc-500 mt-4">
                <span className="animate-pulse">▌</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
);

// ─── Section: SOCIAL PROOF BAND ────────────────────────────────────

const SocialProof = () => (
  <section
    className="border-y border-white/5 bg-white/[0.015] py-6"
    data-testid="landing-social-proof"
  >
    <div className="max-w-7xl mx-auto px-6 lg:px-12">
      <div className="flex flex-wrap items-center justify-center gap-x-10 gap-y-3 text-[10px] uppercase tracking-[3px] text-zinc-500 font-semibold">
        <span>Peer-reviewed architecture</span>
        <span className="text-zinc-700">·</span>
        <span>Self-hostable</span>
        <span className="text-zinc-700">·</span>
        <span>No biometrics</span>
        <span className="text-zinc-700">·</span>
        <span>Post-quantum hybrid</span>
        <span className="text-zinc-700">·</span>
        <span>Never affiliated with any government</span>
      </div>
    </div>
  </section>
);

// ─── Section: HOW IT WORKS ────────────────────────────────────────

const HowItWorks = () => (
  <section
    id="how"
    className="py-24 sm:py-32 scroll-mt-20"
    data-testid="landing-how-it-works"
  >
    <div className="max-w-7xl mx-auto px-6 lg:px-12">
      <div className="max-w-2xl mb-16">
        <div className="text-[10px] uppercase tracking-[3px] text-primary/80 font-semibold mb-4">
          003 · How it works
        </div>
        <h2 className="text-4xl sm:text-5xl font-bold tracking-tighter text-white">
          Three mechanisms. Zero trust.
        </h2>
        <p className="mt-5 text-lg text-zinc-400 leading-relaxed">
          Government Shield isn't an app that happens to encrypt things. It's
          a protocol where the server can't read you — even if it wanted to.
        </p>
      </div>

      <div className="grid md:grid-cols-3 gap-px bg-white/10 border border-white/10 rounded-md overflow-hidden">
        <HowCard
          num="01"
          icon={KeyRound}
          title="E2E handshake"
          subtitle="RSA-OAEP → AES-GCM"
          visual={<HandshakeAnimation />}
          body="Your browser generates a keypair on signup. The private key never touches the network. When you message someone, we encrypt with their public key; only their private key — on their device — can decrypt."
        />
        <HowCard
          num="02"
          icon={Fingerprint}
          title="Key rotation timeline"
          subtitle="SHA-256 fingerprints"
          visual={<RotationAnimation />}
          body="Every public-key rotation is timestamped + signed. Your client pins the fingerprint you first saw. If it silently changes — the MITM signal — the safety stripe goes red before you send your next message."
        />
        <HowCard
          num="03"
          icon={Timer}
          title="24h auto-destruct"
          subtitle="Server-side sweep"
          visual={<DestructAnimation />}
          body="Messages carry a TTL. The server wipes them 24 hours after they're sent, period. No archive, no backup, no 'deleted but recoverable' safety net. If you need it kept, copy it locally before the timer runs out."
        />
      </div>
    </div>
  </section>
);

const HowCard = ({ num, icon: Icon, title, subtitle, visual, body }) => (
  <div className="bg-[#0A0A0F] p-8 relative group hover:bg-[#0D0D14] transition-colors">
    <div className="flex items-start justify-between mb-8">
      <div className="text-[10px] font-mono text-zinc-600 tracking-widest">
        {num}
      </div>
      <Icon className="w-5 h-5 text-zinc-500" strokeWidth={1.5} />
    </div>

    <div className="h-28 mb-8 relative">{visual}</div>

    <div className="text-[10px] uppercase tracking-[2px] text-primary/70 font-semibold mb-2">
      {subtitle}
    </div>
    <h3 className="text-xl font-bold text-white mb-4 tracking-tight">
      {title}
    </h3>
    <p className="text-sm text-zinc-400 leading-relaxed">{body}</p>
  </div>
);

const HandshakeAnimation = () => (
  <div className="relative h-full flex items-center justify-center">
    <div className="flex items-center gap-6">
      <div className="w-12 h-12 rounded-md border border-white/10 bg-white/[0.02] flex items-center justify-center">
        <KeyRound className="w-5 h-5 text-primary gs-pulse-slow" strokeWidth={1.5} />
      </div>
      <div className="flex flex-col gap-1">
        <div className="h-px w-16 bg-gradient-to-r from-primary/50 to-emerald-400/50 gs-flow-right" />
        <div className="h-px w-16 bg-gradient-to-r from-emerald-400/50 to-primary/50 gs-flow-left" />
      </div>
      <div className="w-12 h-12 rounded-md border border-emerald-400/30 bg-emerald-400/[0.04] flex items-center justify-center">
        <ShieldCheck
          className="w-5 h-5 text-emerald-400"
          strokeWidth={1.5}
        />
      </div>
    </div>
  </div>
);

const RotationAnimation = () => {
  const fingerprints = ['6f5b0cb594d3c80e', 'a1b2c3d4e5f6g7h8', '2e77fa82b914c6d1'];
  return (
    <div className="h-full flex flex-col justify-center gap-2 font-mono text-[11px]">
      {fingerprints.map((fp, i) => (
        <div
          key={fp}
          className="flex items-center gap-3 gs-fade-in"
          style={{ animationDelay: `${i * 0.4}s` }}
        >
          <div
            className={`w-1.5 h-1.5 rounded-full ${
              i === 2 ? 'bg-emerald-400' : 'bg-zinc-600'
            }`}
          />
          <span className={i === 2 ? 'text-emerald-300' : 'text-zinc-500'}>
            {fp}
          </span>
          {i < 2 && (
            <span className="text-zinc-700 text-[9px] uppercase tracking-wider">
              rotated
            </span>
          )}
          {i === 2 && (
            <span className="text-emerald-400 text-[9px] uppercase tracking-wider">
              current
            </span>
          )}
        </div>
      ))}
    </div>
  );
};

const DestructAnimation = () => (
  <div className="h-full flex flex-col justify-center gap-3">
    <div className="flex items-center justify-between text-[11px] font-mono">
      <span className="text-zinc-500">sent</span>
      <span className="text-primary">23h 59m</span>
    </div>
    <div className="h-1 rounded-full bg-white/5 overflow-hidden">
      <div className="h-full bg-gradient-to-r from-primary via-emerald-400 to-red-500 gs-countdown" />
    </div>
    <div className="flex items-center justify-between text-[11px] font-mono text-zinc-600">
      <span>now</span>
      <span>00:00:00</span>
    </div>
  </div>
);

// ─── Section: ANTI-SURVEILLANCE BENTO ──────────────────────────────

const AntiSurveillance = () => (
  <section
    id="features"
    className="py-24 sm:py-32 scroll-mt-20 border-t border-white/5"
    data-testid="landing-anti-surveillance"
  >
    <div className="max-w-7xl mx-auto px-6 lg:px-12">
      <div className="max-w-2xl mb-16">
        <div className="text-[10px] uppercase tracking-[3px] text-primary/80 font-semibold mb-4">
          004 · Anti-surveillance
        </div>
        <h2 className="text-4xl sm:text-5xl font-bold tracking-tighter text-white">
          Built for hostile environments.
        </h2>
        <p className="mt-5 text-lg text-zinc-400 leading-relaxed">
          Encryption isn't enough if someone's standing behind you, or your
          device gets seized. Four deliberate mechanisms cover that.
        </p>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-px bg-white/10 border border-white/10 rounded-md overflow-hidden">
        <FeatureCard
          icon={AlertTriangle}
          title="Panic button"
          body="One click wipes keys, cache, trust ledger, and logs you out. The red shield is always bottom-right; no menus, no confirm dialog."
          accent="red"
        />
        <FeatureCard
          icon={VenetianMask}
          title="Decoy mode"
          body="A second password unlocks a plausible fake account with its own contact list and history. Coercive unlocks reveal nothing real."
          accent="amber"
        />
        <FeatureCard
          icon={Eye}
          title="Hover-to-reveal"
          body="Messages render as blurred pills in your chat log. They only decode under your cursor — shoulder-surfers see nothing."
          accent="blue"
          isHoverDemo
        />
        <FeatureCard
          icon={Monitor}
          title="Screen-capture guard"
          body="Detects screenshot + screen-recording attempts and blacks out the window. Works on Windows, macOS, and Android."
          accent="emerald"
        />
      </div>
    </div>
  </section>
);

const FeatureCard = ({ icon: Icon, title, body, accent, isHoverDemo }) => {
  const [revealed, setRevealed] = useState(!isHoverDemo);
  const accentClass = {
    red: 'text-red-400',
    amber: 'text-amber-400',
    blue: 'text-primary',
    emerald: 'text-emerald-400',
  }[accent];

  return (
    <div
      className="bg-[#0A0A0F] p-8 hover:bg-[#0D0D14] transition-colors group"
      onMouseEnter={() => isHoverDemo && setRevealed(true)}
      onMouseLeave={() => isHoverDemo && setRevealed(false)}
      data-testid={`feature-${title.toLowerCase().replace(/\s+/g, '-')}`}
    >
      <Icon className={`w-6 h-6 ${accentClass} mb-6`} strokeWidth={1.5} />
      <h3 className="text-lg font-bold text-white mb-3 tracking-tight">
        {title}
      </h3>
      <div className="relative">
        <p
          className={`text-sm text-zinc-400 leading-relaxed transition-all duration-300 ${
            !revealed ? 'blur-sm select-none' : 'blur-0'
          }`}
        >
          {body}
        </p>
        {isHoverDemo && !revealed && (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[2px] text-zinc-500">
              <EyeOff className="w-3 h-3" />
              hover to reveal
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// ─── Section: POST-QUANTUM & HARDENED VOICE ────────────────────────
//
// Two cards covering the cryptographic frontier of the product:
// (a) hybrid post-quantum (Kyber-768 KEM + ML-DSA-65 signatures)
//     wrapped around the classical RSA-OAEP/AES-GCM stack, and
// (b) LiveKit-backed group voice with client-side SFrame frame
//     encryption and a six-digit SAS spoken-code verification ritual.
// These are the two surfaces most likely to draw a "but does Signal
// do that?" objection — surfaced before the comparison table so the
// table's PQ row reads as a confirmed claim, not a marketing flex.

const PostQuantumAndVoice = () => (
  <section
    id="hardened"
    className="py-24 sm:py-32 scroll-mt-20 border-t border-white/5"
    data-testid="landing-hardened"
  >
    <div className="max-w-7xl mx-auto px-6 lg:px-12">
      <div className="max-w-2xl mb-16">
        <div className="text-[10px] uppercase tracking-[3px] text-primary/80 font-semibold mb-4">
          005 · Hardened stack
        </div>
        <h2 className="text-4xl sm:text-5xl font-bold tracking-tighter text-white">
          Post-quantum &amp; encrypted voice.
        </h2>
        <p className="mt-5 text-lg text-zinc-400 leading-relaxed">
          Two layers most messengers don't ship: a hybrid post-quantum
          envelope wrapped around every message, and group voice calls
          where the SFU never sees the audio in the clear.
        </p>
      </div>

      <div className="grid md:grid-cols-2 gap-px bg-white/10 border border-white/10 rounded-md overflow-hidden">
        {/* Post-quantum card */}
        <div
          className="bg-[#0A0A0F] p-8 hover:bg-[#0D0D14] transition-colors"
          data-testid="hardened-pq-card"
        >
          <div className="flex items-start justify-between mb-8">
            <div className="text-[10px] font-mono text-zinc-600 tracking-widest">
              KEM · Kyber-768
            </div>
            <Atom className="w-5 h-5 text-violet-300" strokeWidth={1.5} />
          </div>

          {/* Visual: hybrid envelope diagram */}
          <div className="h-28 mb-8 relative font-mono text-[10px]">
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="flex items-center gap-3">
                <div className="px-3 py-2 rounded-md border border-white/10 bg-white/[0.02]">
                  <div className="text-zinc-500">classical</div>
                  <div className="text-zinc-200 mt-0.5">RSA · AES-GCM</div>
                </div>
                <div className="text-zinc-600">⊕</div>
                <div className="px-3 py-2 rounded-md border border-violet-300/30 bg-violet-300/[0.04]">
                  <div className="text-violet-300/70">post-quantum</div>
                  <div className="text-violet-200 mt-0.5">Kyber · ML-DSA</div>
                </div>
              </div>
            </div>
          </div>

          <div className="text-[10px] uppercase tracking-[2px] text-violet-300/80 font-semibold mb-2">
            Hybrid envelope
          </div>
          <h3 className="text-xl font-bold text-white mb-4 tracking-tight">
            Quantum-resistant by default
          </h3>
          <p className="text-sm text-zinc-400 leading-relaxed">
            Every message is double-wrapped: the classical RSA-OAEP /
            AES-GCM session key is shipped alongside a Kyber-768 KEM
            ciphertext. A signature from ML-DSA-65 binds the two. If a
            future quantum break compromises one half, the other still
            holds. Harvest-now-decrypt-later loses its target.
          </p>
        </div>

        {/* LiveKit voice card */}
        <div
          className="bg-[#0A0A0F] p-8 hover:bg-[#0D0D14] transition-colors"
          data-testid="hardened-voice-card"
        >
          <div className="flex items-start justify-between mb-8">
            <div className="text-[10px] font-mono text-zinc-600 tracking-widest">
              SFU · LiveKit · SFrame
            </div>
            <Radio className="w-5 h-5 text-emerald-400" strokeWidth={1.5} />
          </div>

          {/* Visual: SAS code mock */}
          <div className="h-28 mb-8 relative font-mono">
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-2">
              <div className="text-[9px] uppercase tracking-[2px] text-zinc-500">
                read aloud · both must match
              </div>
              <div className="flex gap-1.5">
                {['4', '7', '2', '9', '1', '5'].map((digit, i) => (
                  <div
                    key={i}
                    className="w-7 h-9 rounded border border-emerald-400/30 bg-emerald-400/[0.04] flex items-center justify-center text-emerald-300 text-base font-bold gs-fade-in"
                    style={{ animationDelay: `${i * 0.08}s` }}
                  >
                    {digit}
                  </div>
                ))}
              </div>
              <div className="flex items-center gap-1.5 text-[9px] uppercase tracking-[2px] text-emerald-400/80">
                <Mic className="w-3 h-3" />
                SAS · spoken-code verified
              </div>
            </div>
          </div>

          <div className="text-[10px] uppercase tracking-[2px] text-emerald-400/80 font-semibold mb-2">
            Group voice · E2E
          </div>
          <h3 className="text-xl font-bold text-white mb-4 tracking-tight">
            SFU you can shout at
          </h3>
          <p className="text-sm text-zinc-400 leading-relaxed">
            Audio is encrypted on your device with SFrame before it
            ever reaches our LiveKit SFU — the relay forwards opaque
            frames it cannot decode. After connect, every participant
            sees a six-digit code derived from the room state. Read it
            aloud; mismatch and you walk away.
          </p>
        </div>
      </div>
    </div>
  </section>
);

// ─── Section: COMPARISON TABLE ─────────────────────────────────────

const ComparisonTable = () => {
  const rows = [
    ['Always-on E2E encryption', true, true, true, true],
    ['Post-quantum hybrid encryption', true, false, false, false],
    ['E2E group voice (SFU can\'t decode)', true, false, false, false],
    ['24h auto-destruct built-in', true, false, false, false],
    ['Invite-only access', true, false, false, false],
    ['Client-side key generation', true, false, false, true],
    ['No biometric requirement', true, false, false, true],
    ['Self-hostable', true, false, false, false],
    ['Panic button + decoy mode', true, false, false, false],
    ['No analytics, no ads', true, false, false, true],
  ];
  return (
    <section
      className="py-24 sm:py-32 border-t border-white/5"
      data-testid="landing-comparison"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="max-w-2xl mb-16">
          <div className="text-[10px] uppercase tracking-[3px] text-primary/80 font-semibold mb-4">
            006 · Comparison
          </div>
          <h2 className="text-4xl sm:text-5xl font-bold tracking-tighter text-white">
            Against the rest.
          </h2>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full min-w-[640px]">
            <thead>
              <tr className="border-b border-white/10">
                <th className="text-left py-4 text-[11px] font-normal uppercase tracking-[2px] text-zinc-500">
                  Capability
                </th>
                <th className="py-4 text-[11px] font-semibold uppercase tracking-[2px] text-white">
                  Gov Shield
                </th>
                <th className="py-4 text-[11px] font-normal uppercase tracking-[2px] text-zinc-500">
                  Discord
                </th>
                <th className="py-4 text-[11px] font-normal uppercase tracking-[2px] text-zinc-500">
                  WhatsApp
                </th>
                <th className="py-4 text-[11px] font-normal uppercase tracking-[2px] text-zinc-500">
                  Signal
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map(([label, ...flags]) => (
                <tr
                  key={label}
                  className="border-b border-white/5 hover:bg-white/[0.015] transition-colors"
                >
                  <td className="py-4 text-sm text-zinc-300">{label}</td>
                  {flags.map((v, i) => (
                    <td key={i} className="py-4 text-center">
                      {v ? (
                        <Check
                          className={`w-4 h-4 mx-auto ${
                            i === 0 ? 'text-emerald-400' : 'text-emerald-400/60'
                          }`}
                          strokeWidth={2.5}
                        />
                      ) : (
                        <XIcon
                          className="w-4 h-4 mx-auto text-zinc-700"
                          strokeWidth={2}
                        />
                      )}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
};

// ─── Section: DOWNLOADS ────────────────────────────────────────────

const Downloads = () => {
  const items = [
    { label: 'Windows', Icon: Monitor, testId: 'download-win-btn', href: '/api/desktop/download/windows' },
    { label: 'macOS', Icon: Apple, testId: 'download-mac-btn', href: '/api/desktop/download/mac-arm64' },
    { label: 'Linux', Icon: Terminal, testId: 'download-linux-btn', href: '/api/desktop/download/linux-appimage' },
    { label: 'Android', Icon: Download, testId: 'download-android-btn', href: '/api/desktop/download/android' },
  ];
  return (
    <section
      className="py-24 sm:py-32 border-t border-white/5"
      data-testid="landing-downloads"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="max-w-2xl mb-12">
          <div className="text-[10px] uppercase tracking-[3px] text-primary/80 font-semibold mb-4">
            007 · Run it
          </div>
          <h2 className="text-4xl sm:text-5xl font-bold tracking-tighter text-white">
            Native clients.
          </h2>
          <p className="mt-5 text-lg text-zinc-400 leading-relaxed">
            Same codebase, no browser surface to attack. Run your own
            server with the Docker bundle if you don't trust ours — that's
            the whole point.
          </p>
        </div>
        <div className="flex flex-wrap gap-3">
          {items.map(({ label, Icon, testId, href }) => (
            <a
              key={label}
              href={href}
              className="inline-flex items-center gap-2 px-5 py-3 rounded-md border border-white/10 bg-white/[0.02] hover:bg-white/[0.05] hover:border-white/20 transition-all text-sm text-white"
              data-testid={testId}
            >
              <Icon className="w-4 h-4" strokeWidth={1.5} />
              {label}
            </a>
          ))}
          <Link
            to="/selfhost"
            className="inline-flex items-center gap-2 px-5 py-3 rounded-md border border-emerald-400/30 bg-emerald-400/[0.04] hover:bg-emerald-400/[0.08] transition-all text-sm text-emerald-300"
            data-testid="download-selfhost-btn"
          >
            <ShieldCheck className="w-4 h-4" strokeWidth={1.5} />
            Self-host bundle
          </Link>
        </div>
      </div>
    </section>
  );
};

// ─── Section: LIVING SIGNALS ──────────────────────────────────────
//
// Three one-line proofs that the product's distinctive rituals
// actually exist, each linking to a deeper auditable surface.
// Sits between Downloads and FAQ so visitors who just absorbed
// "here's how to install it" see tangible evidence of differentiation
// before their objection-shaped brain hits the FAQ.
//
// Deliberately subtle — four lines of copy, no screenshots, no
// animations. The assumption is that the visitor who cares will
// click through to `/halo`, `/privacy/sealed-sender-demo`, and
// `/manifesto` for the actual proof. This section is a map, not a
// pitch.

const LIVING_SIGNALS = [
  {
    label: 'Halo',
    Icon: Sparkles,
    accent: 'text-primary/90',
    body: 'A zero-PII PNG of your rotation rhythm + voice-verification tally. Share the ritual; keep the contacts.',
    cta: 'See the halo →',
    href: '/halo',
    testId: 'living-signal-halo',
  },
  {
    label: 'Sealed sender',
    Icon: FileSearch,
    accent: 'text-emerald-300/90',
    body: "The `messages` row carries no sender_id. The server relays authenticated tokens, not identities — audit the wire schema yourself.",
    cta: 'See the audit →',
    href: '/privacy/sealed-sender-demo',
    testId: 'living-signal-sealed-sender',
  },
  {
    label: 'Voice verify',
    Icon: Mic,
    accent: 'text-violet-300/90',
    body: 'Every group call ends with a six-digit SAS code derived from the room state. Read it aloud; mismatch and the line is tapped.',
    cta: 'Read the principles →',
    href: '/manifesto',
    testId: 'living-signal-manifesto',
  },
  {
    label: 'Live witness',
    Icon: Activity,
    accent: 'text-amber-300/90',
    body: 'A public ping of the SFU plus the current warrant-canary fingerprint. Auditable from any browser, no account required.',
    cta: 'Open transparency →',
    href: '/transparency',
    testId: 'living-signal-transparency',
  },
];

const LivingSignals = () => (
  <section
    className="py-24 sm:py-32 border-t border-white/5"
    data-testid="landing-living-signals"
  >
    <div className="max-w-7xl mx-auto px-6 lg:px-12">
      <div className="max-w-2xl mb-12">
        <div className="text-[10px] uppercase tracking-[3px] text-primary/80 font-semibold mb-4">
          008 · Proofs
        </div>
        <h2 className="text-4xl sm:text-5xl font-bold tracking-tighter text-white">
          Living signals.
        </h2>
        <p className="mt-5 text-lg text-zinc-400 leading-relaxed">
          Three surfaces you can audit without an account. Every
          claim on this page has a corresponding test you can run
          on the wire, a page you can inspect, or a ritual you can
          watch two humans perform.
        </p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {LIVING_SIGNALS.map(({ label, Icon, accent, body, cta, href, testId }) => (
          <Link
            key={label}
            to={href}
            className="group block p-6 rounded-lg border border-white/10 bg-white/[0.02] hover:bg-white/[0.04] hover:border-white/20 transition-all"
            data-testid={testId}
          >
            <div className={`flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] ${accent} mb-4`}>
              <Icon className="w-4 h-4" strokeWidth={1.5} />
              {label}
            </div>
            <p className="text-sm text-zinc-300 leading-relaxed mb-5">
              {body}
            </p>
            <div className={`text-xs font-semibold flex items-center gap-1 ${accent} group-hover:gap-2 transition-all`}>
              {cta}
            </div>
          </Link>
        ))}
      </div>
    </div>
  </section>
);

// ─── Section: FAQ ──────────────────────────────────────────────────

const FAQ_ITEMS = [
  {
    q: 'Is using Government Shield legal?',
    a: "Yes, in every jurisdiction where strong encryption is legal for civilians — which is most of them. We don't encourage or enable illegal activity, and we will respond to valid legal process for the metadata we do hold (account creation date, last-seen timestamp). We can't disclose message content because we don't have it.",
  },
  {
    q: 'What happens if I lose my phone?',
    a: 'Your 64-character Recovery Key (shown once, at signup) is the only way to log back in from a new device. If you lose both the device and the Recovery Key, the account is genuinely unrecoverable — we store only a bcrypt hash of the key, not the key itself. Write it down. Print it. Put it somewhere you trust.',
  },
  {
    q: 'Can you read my messages?',
    a: "No. Message content is encrypted with AES-GCM using a per-message key that's wrapped with the recipient's RSA-OAEP public key. Only the recipient's device — which holds the matching private key — can unwrap it. The server sees ciphertext and routing metadata.",
  },
  {
    q: "What if the server is seized or compromised?",
    a: 'Nothing usable comes out. No plaintext archive exists — messages are wiped 24h after send, and the pre-wipe state is ciphertext the server can\'t decrypt. Account metadata (email, username, bcrypt hashes) would be exposed, which is why we recommend self-hosting for high-threat-model users.',
  },
  {
    q: 'Can the code be audited?',
    a: "Yes — Government Shield is open-source. The crypto primitives are the browser's Web Crypto API (audited by platform vendors). The wire protocol, message lifecycle, and all server routes are inspectable. Self-hosters run the exact same code.",
  },
  {
    q: 'Can I self-host?',
    a: "Yes. Download the self-host bundle (Docker Compose + launch scripts) from the /selfhost page. The first user to register on a fresh install becomes the server owner automatically — no transferable, no backdoor. Sub-admins can be promoted, but the owner position is permanent.",
  },
  {
    q: "Why no biometrics?",
    a: "Because biometric unlocks create coercive leverage — legally in some jurisdictions you can be compelled to press your finger or look at a camera, but not to disclose a password. We want the account to be yours behind something the state can't physically extract.",
  },
  {
    q: 'Is this affiliated with any government?',
    a: "No. Government Shield is an independent project and will never be affiliated with any government, intelligence agency, or law-enforcement body. The name is intentional — we protect against government-scale surveillance, we're not employed by one.",
  },
];

const FAQ = () => (
  <section
    id="faq"
    className="py-24 sm:py-32 scroll-mt-20 border-t border-white/5"
    data-testid="landing-faq"
  >
    <div className="max-w-4xl mx-auto px-6 lg:px-12">
      <div className="mb-16">
        <div className="text-[10px] uppercase tracking-[3px] text-primary/80 font-semibold mb-4">
          009 · FAQ
        </div>
        <h2 className="text-4xl sm:text-5xl font-bold tracking-tighter text-white">
          Questions, answered.
        </h2>
      </div>

      <Accordion
        type="single"
        collapsible
        className="w-full"
        data-testid="faq-accordion"
      >
        {FAQ_ITEMS.map((item, i) => (
          <AccordionItem
            key={item.q}
            value={`item-${i}`}
            className="border-b border-white/10"
            data-testid={`faq-item-${i}`}
          >
            <AccordionTrigger className="py-6 text-left text-lg font-medium text-white hover:text-primary hover:no-underline tracking-tight">
              {item.q}
            </AccordionTrigger>
            <AccordionContent className="pb-6 text-sm text-zinc-400 leading-relaxed font-mono">
              {item.a}
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    </div>
  </section>
);

// ─── Section: FINAL CTA ────────────────────────────────────────────

const FinalCTA = () => (
  <section
    className="py-24 sm:py-32 border-t border-white/10 relative overflow-hidden"
    data-testid="landing-final-cta"
  >
    <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(59,130,246,0.08),transparent_60%)] pointer-events-none" />
    <div className="relative max-w-4xl mx-auto px-6 lg:px-12 text-center">
      <h2 className="text-5xl sm:text-6xl font-black tracking-tighter text-white leading-[0.95]">
        The room is quiet.
        <br />
        <span className="text-primary">Ask for an invite.</span>
      </h2>
      <p className="mt-6 text-lg text-zinc-400 max-w-xl mx-auto">
        Current members review every request. If approved, you get a
        single-use code that expires in 48 hours.
      </p>
      <div className="mt-10 flex flex-wrap gap-3 justify-center">
        <Button
          asChild
          size="lg"
          className="bg-primary hover:bg-primary/90 text-white font-semibold gap-2 rounded-md"
          data-testid="final-waitlist-btn"
        >
          <Link to="/waitlist">
            Request access
            <ArrowRight className="w-4 h-4" />
          </Link>
        </Button>
        <Button
          asChild
          size="lg"
          variant="outline"
          className="border-white/15 bg-transparent hover:bg-white/5 text-white rounded-md"
          data-testid="final-manifesto-btn"
        >
          <Link to="/manifesto">Read the manifesto</Link>
        </Button>
      </div>
    </div>
  </section>
);

// ─── Section: FOOTER ───────────────────────────────────────────────

const Footer = () => (
  <footer
    className="border-t border-white/10 bg-black py-12"
    data-testid="landing-footer"
  >
    <div className="max-w-7xl mx-auto px-6 lg:px-12">
      <div className="flex flex-wrap items-start justify-between gap-8">
        <div className="flex items-center gap-2">
          <ShieldCheck className="w-5 h-5 text-primary" strokeWidth={1.75} />
          <span className="text-sm font-semibold tracking-tight text-white">
            Government Shield
          </span>
        </div>
        <div className="flex flex-wrap gap-x-8 gap-y-3 text-sm text-zinc-500">
          <Link to="/manifesto" className="hover:text-white transition-colors" data-testid="footer-manifesto-link">
            Manifesto
          </Link>
          <Link to="/legal" className="hover:text-white transition-colors flex items-center gap-1.5" data-testid="footer-legal-link">
            <Scale className="w-3.5 h-3.5" /> Legal
          </Link>
          <Link to="/legal/privacy" className="hover:text-white transition-colors flex items-center gap-1.5" data-testid="footer-privacy-link">
            <FileText className="w-3.5 h-3.5" /> Privacy
          </Link>
          <Link to="/selfhost" className="hover:text-white transition-colors" data-testid="footer-selfhost-link">
            Self-host
          </Link>
          <a
            href="https://github.com/"
            target="_blank"
            rel="noreferrer noopener"
            className="hover:text-white transition-colors flex items-center gap-1.5"
            data-testid="footer-github-link"
          >
            <Github className="w-3.5 h-3.5" /> GitHub
          </a>
        </div>
      </div>

      <div className="mt-10 pt-8 border-t border-white/5">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
          <GovernmentDisclaimer />
          <WarrantCanaryChip />
        </div>
        <p className="mt-6 text-[11px] text-zinc-600 font-mono">
          © 2026 · An independent project · will never be affiliated with any
          government, intelligence agency, or law-enforcement body.
        </p>
        {/* Second OOB anchor for the warrant-canary public key — a
            cloned/spoofed deployment would have to tamper with the
            manifesto, this footer, AND the .well-known/canary-fingerprint.txt
            in lockstep to evade detection. */}
        <p
          className="mt-2 text-[10px] text-zinc-700 font-mono tracking-[0.15em]"
          data-testid="footer-canary-fingerprint"
        >
          canary · afcb 5e69 0756 ff16 3f84 69f6 95b8 4652 · verify at{' '}
          <Link
            to="/manifesto#canary"
            className="underline decoration-dotted hover:text-zinc-400 transition-colors"
          >
            /manifesto
          </Link>{' '}
          or{' '}
          <a
            href="/.well-known/canary-fingerprint.txt"
            className="underline decoration-dotted hover:text-zinc-400 transition-colors"
            target="_blank"
            rel="noreferrer noopener"
          >
            /.well-known/canary-fingerprint.txt
          </a>
        </p>
        <p
          className="mt-2 text-[10px] text-zinc-700 font-mono tracking-[0.15em]"
          data-testid="footer-matrix-bridge"
        >
          matrix bridge · outbound broadcast only · live witness at{' '}
          <Link
            to="/transparency"
            className="underline decoration-dotted hover:text-zinc-400 transition-colors"
          >
            /transparency
          </Link>
        </p>
      </div>
    </div>
  </footer>
);

// ─── Page root ─────────────────────────────────────────────────────

export const LandingPage = () => {
  // Smooth scroll for in-page anchors
  useEffect(() => {
    document.documentElement.style.scrollBehavior = 'smooth';
    return () => {
      document.documentElement.style.scrollBehavior = '';
    };
  }, []);

  return (
    <div className="min-h-screen bg-[#05050A] text-white antialiased" data-testid="landing-page">
      <Nav />
      <main>
        <Hero />
        <SocialProof />
        <HowItWorks />
        <AntiSurveillance />
        <PostQuantumAndVoice />
        <ComparisonTable />
        <Downloads />
        <LivingSignals />
        <FAQ />
        <FinalCTA />
      </main>
      <Footer />
    </div>
  );
};

export default LandingPage;
